import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';

import { MessageOutlined } from '@ant-design/icons';
import { Alert, App, Checkbox, List, Modal, ModalFuncProps, Spin, Typography } from 'antd';

import * as actions from '../store/actions';

import { isBoolean, isEmpty } from 'lodash';
import { SmsMigrationData } from '../models';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

const { Text } = Typography;

function useForceUpdate() {
  const [, setValue] = useState<number>(0);
  return () => setValue((value) => value + 1);
}

export type SmsMigrateProps = {
  showModal: boolean;
  onCloseModal: () => void;
  resetAndRefresh: () => void;
};

const SmsMigrate = ({ showModal, resetAndRefresh, onCloseModal }: SmsMigrateProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const forceUpdate = useForceUpdate();

  const { modal } = App.useApp();

  const [isModified, setIsModified] = useState<boolean>(false);
  const [initiatingDone, setInitiatingDone] = useState<boolean>(false);
  const [isMigrate, setIsMigrate] = useState<boolean>(false);

  const { startMigrationResult, startMigrationLoading, saveMigrationLoading, messageControlParameters } = useSelector(
    (state: RootState) => state.smses,
  );

  useEffect(() => {
    if (!isEmpty(messageControlParameters) && messageControlParameters.smsMigrateIsCompleted === null) {
      modal.confirm({
        title: 'New SMS system',
        content: (
          <div>
            <p>
              Great news.Here is the new SMS system.This is why we need to migrate the old SMS to the new system and we may need your help to do so.
            </p>
            <p>
              After clicking OK, we will process all your old messages.If there are problems with determining the phone number, we will ask you to
              solve them.If you don't know the correct choice, just leave them and these messages will be displayed in the middle of the SMS message
              window.
            </p>
            <p>
              Or you can cancel it, skip the migration and use the new SMS system right now.You can migrate it later on the Actions - Migrate old
              SMSes to the new
            </p>
          </div>
        ),
        icon: <MessageOutlined />,
        okText: 'Ok',
        cancelText: 'Cancel',
        onOk: () => {
          setIsMigrate(true);
          setInitiatingDone(true);
        },
        onCancel: () => {
          setIsMigrate(false);
          setInitiatingDone(true);
        },
      } as ModalFuncProps);
    } else {
      setIsMigrate(true);
      setInitiatingDone(true);
    }
  }, []);

  useEffect(() => {
    if (isMigrate === true) {
      dispatch<Promise<SmsMigrationData>>(actions.startMigration()).then((x) => {
        if (isBoolean(x.migrationIsDone) && x.migrationIsDone === true) {
          dispatch(actions.getMessageControlParameters()).then(() => {
            modal.success({
              title: 'Migration are saved successfully',
              onOk: () => {
                resetAndRefresh();
                onCloseModal();
              },
            });
          });
        }
      });
    } else if (isMigrate === false) {
      dispatch(actions.saveMigration({ isCancelMigration: true }));
      onCloseModal();
    }
  }, [isMigrate]);

  useEffect(() => {
    if (saveMigrationLoading) {
      modal.info({
        title: 'Save migration result',
        content: 'Wait for the save to complete. This may take some time.',
        icon: <MessageOutlined />,
      });
    }
  }, [saveMigrationLoading]);

  const onClose = (isModifiedModal: boolean) => {
    if (isModifiedModal) {
      modal.confirm({
        title: 'All changes will be lost. Are you sure you want to close?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          onCloseModal();
          return dispatch(actions.saveMigration({ isCancelMigration: true }));
        },
      });
    } else {
      onCloseModal();
      return dispatch(actions.saveMigration({ isCancelMigration: true }));
    }
  };

  const onFinish = () => {
    if (!checkIsAllSelected()) {
      modal.confirm({
        title: 'Not all phone number is selected. Those SMSes will not be in the SMS list. Are you sure you want to continue?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: saveMigration,
      });
    } else {
      return saveMigration();
    }
  };

  const saveMigration = () => {
    return new Promise((resolve, reject) => {
      dispatch(actions.saveMigration({ smsMigrationData: startMigrationResult }))
        .then(() => {
          dispatch(actions.getMessageControlParameters()).then(() => {
            modal.success({
              title: 'Your settings are saved successfully',
              onOk: () => {
                resetAndRefresh();
                onCloseModal();
                resolve(undefined);
              },
            } as ModalFuncProps);
          });
        })
        .catch((error: any) => reject(error));
    });
  };

  const onPhoneNumberCheckboxChange = (event: CheckboxChangeEvent, phoneNumber: string) => {
    var newValue = event.target.checked ?? false;

    if (newValue) {
      addToMyNumberList(phoneNumber);
    } else {
      removeFromMyNumberList(phoneNumber);
    }

    forceUpdate();

    if (!isModified) {
      setIsModified(true);
    }
  };

  const addToMyNumberList = (newMyNumber: string) => {
    if (startMigrationResult && !startMigrationResult?.myNumberList?.includes(newMyNumber)) {
      startMigrationResult.myNumberList.push(newMyNumber);

      var smsWithNewMyNumberList = startMigrationResult.unknownList.filter((x) => x.phoneNumber1 === newMyNumber || x.phoneNumber2 === newMyNumber);

      smsWithNewMyNumberList.forEach((x) => {
        if (x.phoneNumber1 === newMyNumber) {
          x.isPhoneNumber1Mine = true;

          if (x.isPhoneNumber2Mine === true) {
            removeFromMyNumberList(x.phoneNumber2);
          }
        } else {
          x.isPhoneNumber2Mine = true;

          if (x.isPhoneNumber1Mine === true) {
            removeFromMyNumberList(x.phoneNumber1);
          }
        }
      });
    }
  };

  const removeFromMyNumberList = (removeMyNumber: string) => {
    if (startMigrationResult && startMigrationResult?.myNumberList?.includes(removeMyNumber)) {
      startMigrationResult.myNumberList = startMigrationResult.myNumberList.filter((x) => x !== removeMyNumber);

      var smsWithRemoveMyNumberList = startMigrationResult?.unknownList?.filter(
        (x) => x.phoneNumber1 === removeMyNumber || x.phoneNumber2 === removeMyNumber,
      );

      smsWithRemoveMyNumberList.forEach((x) => {
        if (x.phoneNumber1 === removeMyNumber) {
          x.isPhoneNumber1Mine = false;
        } else {
          x.isPhoneNumber2Mine = false;
        }
      });
    }
  };

  const checkIsAllSelected = () => {
    return (
      startMigrationResult?.unknownList?.findIndex((element) => element.isPhoneNumber1Mine !== true && element.isPhoneNumber2Mine !== true) === -1
    );
  };

  return (
    <>
      <Modal
        title="SMS migrate"
        open={showModal}
        onCancel={() => onClose(isModified)}
        onClose={() => onClose(isModified)}
        onOk={onFinish}
        okButtonProps={{
          loading: !initiatingDone || startMigrationLoading || saveMigrationLoading,
        }}
        width={700}
        className="sms-migrate-wrapper"
      >
        <Spin spinning={!initiatingDone || startMigrationLoading || saveMigrationLoading}>
          <p>
            Below is a list of SMS with unknown recipients and with unknown phone numbers.Select a phone number that is your number or was your
            number.With this information, we can take the name from the SMS and the phone number for future chat.{' '}
          </p>
          <p>
            Chat with messages is ready to migrate:{' '}
            {(startMigrationResult?.smsWithKnownNumberList?.length ?? 0) + (startMigrationResult?.smsWithChatIdList?.length ?? 0)}
          </p>
          <p> Chat with unknown phones: {startMigrationResult?.unknownList?.length ?? 0} </p>
          <List
            size="small"
            bordered
            dataSource={startMigrationResult?.unknownList ?? []}
            renderItem={(migrationItem) => (
              <List.Item>
                <Alert
                  style={{ width: '100%' }}
                  description={
                    <div>
                      <div style={{ display: 'flex' }}>
                        <Checkbox
                          onChange={(event: CheckboxChangeEvent) => onPhoneNumberCheckboxChange(event, migrationItem.phoneNumber1)}
                          checked={migrationItem.isPhoneNumber1Mine}
                        >
                          {migrationItem.phoneNumber1}
                        </Checkbox>
                        <Text> {migrationItem.namePhoneNumber1} </Text>
                        {migrationItem.isPhoneNumber1Mine ? (
                          <Text className="my-number-mark" mark>
                            My number
                          </Text>
                        ) : null}
                      </div>
                      <div style={{ display: 'flex' }}>
                        <Checkbox
                          onChange={(event: CheckboxChangeEvent) => onPhoneNumberCheckboxChange(event, migrationItem.phoneNumber2)}
                          checked={migrationItem.isPhoneNumber2Mine}
                        >
                          {migrationItem.phoneNumber2}
                        </Checkbox>
                        <Text> {migrationItem.namePhoneNumber2} </Text>
                        {migrationItem.isPhoneNumber2Mine ? (
                          <Text className="my-number-mark" mark>
                            My number
                          </Text>
                        ) : null}
                      </div>
                    </div>
                  }
                  type={migrationItem.isPhoneNumber1Mine || migrationItem.isPhoneNumber2Mine ? 'success' : 'error'}
                />
              </List.Item>
            )}
          />
        </Spin>
      </Modal>
    </>
  );
};

export default SmsMigrate;
